import React, { useState, useEffect, useRef } from 'react';
import { api } from '../store/useStore';
import useStore from '../store/useStore';

export default function DeviceCodeModal({ onClose, onSuccess, accountToReconnect }) {
  const { settings } = useStore();
  const isReconnect = !!accountToReconnect;
  const [step, setStep] = useState(isReconnect ? 'code' : 'provider'); // provider | code | success
  const [provider, setProvider] = useState(isReconnect ? accountToReconnect.provider : null);
  const [session, setSession] = useState(null);
  const [error, setError] = useState('');
  const [secondsLeft, setSecondsLeft] = useState(0);
  const pollRef = useRef(null);
  const countdownRef = useRef(null);

  const timerEnabled = settings.timer_display_enabled !== 'false';

  useEffect(() => () => {
    clearInterval(pollRef.current);
    clearInterval(countdownRef.current);
  }, []);

  // Auto-start reconnect flow on mount
  useEffect(() => {
    if (isReconnect) startFlow(accountToReconnect.provider); // intentional empty deps
  }, []); // eslint-disable-line

  const startFlow = async (prov) => {
    setProvider(prov);
    setStep('code');
    setError('');
    try {
      const endpoint = isReconnect
        ? `/api/emails/accounts/${accountToReconnect.id}/reconnect`
        : '/connect/init';
      const { data } = isReconnect
        ? await api.post(endpoint)
        : await api.post(endpoint, { provider: prov });
      setSession(data);
      const timeout = parseInt(settings.device_code_timeout_seconds || 600);
      setSecondsLeft(timeout);

      clearInterval(countdownRef.current);
      countdownRef.current = setInterval(() => {
        setSecondsLeft(s => {
          if (s <= 1) { clearInterval(countdownRef.current); return 0; }
          return s - 1;
        });
      }, 1000);

      clearInterval(pollRef.current);
      pollRef.current = setInterval(async () => {
        try {
          const { data: status } = await api.get(`/connect/session/${data.session_token}/status`);
          if (status.status === 'connected') {
            clearInterval(pollRef.current);
            clearInterval(countdownRef.current);
            setStep('success');
            onSuccess?.(status);
          } else if (status.status === 'expired' || status.status === 'cancelled') {
            clearInterval(pollRef.current);
            setError('Session expired. Please try again.');
            setStep('provider');
          }
        } catch {}
      }, (data.poll_interval || 5) * 1000);

    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to start. Check provider config.');
      setStep('provider');
    }
  };

  const cancel = async () => {
    clearInterval(pollRef.current);
    clearInterval(countdownRef.current);
    if (session?.session_token) {
      await api.delete(`/connect/session/${session.session_token}`).catch(() => {});
    }
    onClose();
  };

  const copyCode = () => {
    if (session?.user_code) navigator.clipboard.writeText(session.user_code).catch(() => {});
  };

  const fmt = (s) => {
    const m = String(Math.floor(s / 60)).padStart(2, '0');
    const sec = String(s % 60).padStart(2, '0');
    return `${m}:${sec}`;
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && cancel()}>
      <div className="modal">
        {step === 'provider' && !isReconnect && (
          <>
            <div className="modal-title">Connect Email Account</div>
            {error && <div className="error-banner">{error}</div>}
            <p className="text-sm text-muted mb-4">Select your email provider to begin the secure connection process.</p>
            <button className="btn btn-ghost" style={{ width: '100%', justifyContent: 'flex-start', marginBottom: 10, padding: '14px 16px' }}
              onClick={() => startFlow('microsoft')}>
              <span style={{ fontSize: 20 }}>🪟</span> Microsoft 365 / Outlook
            </button>
            <button className="btn btn-ghost" style={{ width: '100%', justifyContent: 'flex-start', padding: '14px 16px' }}
              onClick={() => startFlow('google')}>
              <span style={{ fontSize: 20 }}>✉️</span> Google / Gmail
            </button>
            <div className="modal-actions">
              <button className="btn btn-ghost" onClick={cancel}>Cancel</button>
            </div>
          </>
        )}

        {step === 'code' && session && (
          <>
            <div className="modal-title">
              {provider === 'microsoft' ? '🪟 Microsoft 365' : '✉️ Google'} — {isReconnect ? 'Reconnect' : 'Sign In'}
            </div>
            {isReconnect && (
              <p className="text-sm text-muted mb-4" style={{ color: 'var(--warning)', marginBottom: 8 }}>
                Re-authorising <strong>{accountToReconnect.email}</strong>
              </p>
            )}
            <div className="text-sm text-muted mb-4">
              <p style={{ marginBottom: 6 }}><strong style={{ color: 'var(--text)' }}>Step 1:</strong> Copy the code below</p>
              <p style={{ marginBottom: 6 }}><strong style={{ color: 'var(--text)' }}>Step 2:</strong> Click <em>Open Login Page</em></p>
              <p><strong style={{ color: 'var(--text)' }}>Step 3:</strong> Paste the code and sign in</p>
            </div>
            <div className="code-box" style={{ marginBottom: 14 }}>
              <span className="code-text">{session.user_code}</span>
              <button className="btn btn-ghost btn-sm" onClick={copyCode}>Copy</button>
            </div>
            <a
              href={session.verification_uri}
              target="_blank"
              rel="noreferrer"
              className="btn btn-success"
              style={{ width: '100%', justifyContent: 'center', marginBottom: 14, textDecoration: 'none' }}
              onClick={copyCode}
            >
              Open Login Page ↗
            </a>
            <div className="flex items-center gap-2" style={{ marginBottom: 14, color: 'var(--text2)', fontSize: 13 }}>
              <span className="spinner" />
              <span>Waiting for sign in…</span>
              {timerEnabled && secondsLeft > 0 && (
                <span style={{ marginLeft: 'auto', color: 'var(--text3)', fontSize: 12 }}>
                  Expires in {fmt(secondsLeft)}
                </span>
              )}
            </div>
            {error && <div className="error-banner">{error}</div>}
            <button className="btn btn-ghost" style={{ width: '100%' }} onClick={cancel}>Cancel</button>
          </>
        )}

        {step === 'success' && (
          <div style={{ textAlign: 'center', padding: '10px 0' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
            <div style={{ fontSize: 18, fontWeight: 600, color: 'var(--success)', marginBottom: 6 }}>
              {isReconnect ? 'Account Reconnected!' : 'Email Connected!'}
            </div>
            <p className="text-sm text-muted" style={{ marginBottom: 20 }}>
              {isReconnect ? 'Your account is active again.' : 'Your inbox has been linked successfully.'}
            </p>
            <button className="btn btn-primary" onClick={onClose}>Done</button>
          </div>
        )}
      </div>
    </div>
  );
}
